/* eslint-disable @typescript-eslint/no-explicit-any */
import Layout from 'components/Layout/Layout';
import PersonnelStatisticsChart from './PersonnelStatisticsChart';
import NoDataText from 'components/Text/NoDataText';
import { useSumArray } from 'hooks/useSumArray';
import useGetEachData from 'hooks/useGetEachData';
import { usePercentagesFromArrayWithTotal } from 'hooks/usePercentagesFromArray';
import { useAtomValue } from 'jotai';
import centerInforIdxAtom from 'atom/centerInforIdxAtom';
import companyIdxAtom from 'atom/companyIdxAtom';
import productAtom from 'atom/productAtom';
import { useQuery } from '@tanstack/react-query';
import { QueryKeys, restfetcher } from 'queryClient';
import usePersonnelXlsx from 'hooks/usePersonnelXlsx';
import commonYearAtom from 'atom/commonYear';
import commonMonthAtom from 'atom/commonMonth';

const PersonnelStatisticsLayout = () => {
  const centerIdx = useAtomValue(centerInforIdxAtom);
  const selectedYear = useAtomValue(commonYearAtom);
  const selectedMonth = useAtomValue(commonMonthAtom);
  const companyIdx = useAtomValue(companyIdxAtom);
  const productNumber = useAtomValue(productAtom);

  const param = {
    companyidx: companyIdx,
    centeridx: centerIdx,
    year: selectedYear,
    month: selectedMonth
  };

  const queryKey = [QueryKeys.PERSONNELCHART, param];

  const getChartData = useQuery({
    queryKey: queryKey,
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'dashboard/employee/stats',
        params: param
      }),
    enabled: !!companyIdx && !!selectedYear
  });

  const { data: chartData } = getChartData;

  const total: number = chartData?.employee?.total;

  const sumArray = useSumArray;

  const { statsDifferentProductNumber, extractedData, extractedLabels } =
    useGetEachData(productNumber, chartData);

  const createRowWithStyles = (
    labels: string[],
    percentages: number[],
    data: number[],
    title: string
  ) => {
    return labels.map((label, index) => {
      const row =
        index === 0
          ? [
              {
                v: title
              },
              { v: label }
            ]
          : [{}, { v: label }];

      row.push({
        v: `${percentages[index]}`
      });

      row.push({
        v: `${data[index].toLocaleString()}`
      });

      return row;
    });
  };

  const getFirstDataPercent = usePercentagesFromArrayWithTotal(
    extractedData[0],
    total
  );
  const getSecondDataPercent = usePercentagesFromArrayWithTotal(
    extractedData[1],
    total
  );

  const firstResult = createRowWithStyles(
    extractedLabels[0].map((label) => label),
    getFirstDataPercent.map((percent) => percent),
    extractedData[0].map((value) => value),
    statsDifferentProductNumber[0].value
  );

  const secondResult = createRowWithStyles(
    extractedLabels[1].map((label) => label),
    getSecondDataPercent.map((percent) => percent),
    extractedData[1].map((age) => age),
    statsDifferentProductNumber[1].value
  );

  const createTotalRow = (
    label: string,
    percentages: string,
    data: number[]
  ) => {
    return [
      {},
      {
        v: label
      },
      {
        v: percentages
      },
      {
        v: `${sumArray(data)}`
      }
    ];
  };

  const commonResults = [
    ...firstResult,
    createTotalRow('합계', '100', extractedData[0]),
    ...secondResult,
    createTotalRow('합계', '100', extractedData[1])
  ];

  if (productNumber === 4 || productNumber === 5) {
    const getThirdDataPercent = usePercentagesFromArrayWithTotal(
      extractedData[2],
      total
    );

    const thirdResult = createRowWithStyles(
      extractedLabels[2].map((label) => label),
      getThirdDataPercent.map((percent) => percent),
      extractedData[2].map((gender) => gender),
      statsDifferentProductNumber[2].value
    );

    commonResults.push(
      ...thirdResult,
      createTotalRow('합계', '100', extractedData[2])
    );
  }

  const { handleClickExcelDownload } = usePersonnelXlsx({
    dataRows: commonResults
  });

  return (
    <>
      <Layout
        title="검사 대상 통계"
        onExcelDownload={handleClickExcelDownload}
        result={chartData?.result}
      >
        {chartData && chartData.result === 'success' ? (
          <>
            <PersonnelStatisticsChart
              extractedData={extractedData}
              extractedLabels={extractedLabels}
              statsDifferentProductNumber={statsDifferentProductNumber}
            />
          </>
        ) : (
          <NoDataText />
        )}
      </Layout>
    </>
  );
};

export default PersonnelStatisticsLayout;
